import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiUsers, FiBriefcase, FiCheckSquare, FiMessageSquare,
  FiTrendingUp, FiClock, FiAlertCircle, FiArrowRight,
  FiActivity, FiCalendar, FiTarget, FiLayers, FiInbox,
} from "react-icons/fi";
import {
  getUsers, getProjects, getAllContacts, getLatestActivity,
} from "../api/api";
import { getTasks } from "../api/tasksApi";
import toast from "react-hot-toast";
import "./Home.css";

// ─── Constants ────────────────────────────────────────────────────────────────
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://api.growthstationco.com";

const ACTIVE_STATUSES = ["active", "remote", "meeting"];

function getAuthHeaders() {
  const token = localStorage.getItem("token") || "";
  return { "Content-Type": "application/json", Authorization: token };
}

function formatLocalYYYYMMDD(d) {
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day   = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeLogStatus(raw) {
  const s = String(raw ?? "").toLowerCase().trim();
  if (!s) return "offline";
  if (["day off", "day-off", "day_off", "dayoff"].includes(s)) return "dayoff";
  if (s.includes("break") || s === "on-break")                  return "break";
  if (["off", "offline"].includes(s))                           return "offline";
  if (["remote", "wfh", "work from home"].includes(s))          return "remote";
  if (["active", "online", "working"].includes(s))              return "active";
  if (["meeting", "in meeting"].includes(s))                    return "meeting";
  return "offline";
}

// ─── Fetch a single user's logs for a date ───────────────────────────────────
async function fetchUserLogsForDate(userId, dateStr) {
  try {
    const res = await fetch(
      `${BASE_URL}/log/user/${userId}?date=${dateStr}`,
      { headers: getAuthHeaders() }
    );
    if (!res.ok) return [];
    const data = await res.json();
    const raw  = Array.isArray(data) ? data : (data?.data ?? []);
    return raw
      .filter((l) => {
        if (!l?.startTime) return false;
        const d = new Date(l.startTime);
        return !isNaN(d.getTime()) && formatLocalYYYYMMDD(d) === dateStr;
      })
      .map((l) => ({ ...l, status: normalizeLogStatus(l.status) }));
  } catch {
    return [];
  }
}

// ─── Compute active ms from logs (same logic as TeamOverview) ────────────────
function computeActiveMs(logs, dateStr) {
  const now     = Date.now();
  const isToday = dateStr === formatLocalYYYYMMDD(new Date());
  const dayEnd  = new Date(dateStr); dayEnd.setHours(23, 59, 59, 999);

  const sorted = [...logs]
    .filter((l) => l.startTime && l.status !== "offline")
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  let activeMs = 0;

  for (let i = 0; i < sorted.length; i++) {
    const log   = sorted[i];
    if (log.status !== "active" && log.status !== "remote" && log.status !== "meeting") continue;

    const start = new Date(log.startTime).getTime();
    if (isNaN(start)) continue;

    let end = null;
    if (log.endTime) {
      end = new Date(log.endTime).getTime();
    } else if (sorted[i + 1]?.startTime) {
      end = new Date(sorted[i + 1].startTime).getTime();
    } else if (isToday) {
      end = now;
    } else {
      continue;
    }

    if (!end || isNaN(end)) continue;
    const duration = Math.max(0, Math.min(end, dayEnd.getTime()) - start);
    activeMs += duration;
  }

  return activeMs;
}

// ─── Check if a day has any active/remote/meeting log ────────────────────────
function dayHasActiveWork(logs) {
  return logs.some((l) => ACTIVE_STATUSES.includes(l.status));
}

// ─── Get all dates in current month up to today ──────────────────────────────
function getDatesThisMonth() {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = now.getMonth();
  const today = formatLocalYYYYMMDD(now);
  const dates = [];
  const d     = new Date(year, month, 1);
  while (d.getMonth() === month && formatLocalYYYYMMDD(d) <= today) {
    dates.push(formatLocalYYYYMMDD(new Date(d)));
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Home() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalUsers:     0,
    activeProjects: 0,
    pendingTasks:   0,
    unreadMessages: 0,
    myTasks:        0,
    myMessages:     0,
    hoursToday:     0,
    daysThisMonth:  0,
  });
  const [loading,        setLoading]        = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  const userName  = currentUser?.userName || currentUser?.email?.split("@")[0] || "User";
  const userRole  = (currentUser?.role || "Employee").toUpperCase();
  const isAdmin   = userRole === "FOUNDER" || userRole === "TEAM_LEADER";
  const isFounder = userRole === "FOUNDER";

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // ── ADMIN ──────────────────────────────────────────────────────────────
      if (isAdmin) {
        const usersRes = await getUsers();
        const users    = usersRes?.data || usersRes || [];

        const projectsRes        = await getProjects();
        const projects           = projectsRes?.data?.data || projectsRes?.data || projectsRes || [];
        const activeProjectsCount = Array.isArray(projects)
          ? projects.filter((p) => p.status === "in progress" || p.status === "in-progress").length
          : 0;

        const tasksRes        = await getTasks();
        const tasks           = tasksRes?.data?.data || tasksRes?.data || tasksRes || [];
        const pendingTasksCount = Array.isArray(tasks)
          ? tasks.filter((t) => {
              const s = t.status?.toLowerCase();
              return s === "done" || s === "completed" || s === "finished";
            }).length
          : 0;

        const contactsRes        = await getAllContacts({ status: "pending" });
        const contacts           = contactsRes?.data || contactsRes || [];
        const unreadMessagesCount = Array.isArray(contacts) ? contacts.length : 0;

        const activityRes  = await getLatestActivity();
        const activityData = activityRes?.data || activityRes || [];

        const formattedActivity = Array.isArray(activityData)
          ? activityData.slice(0, 5).map((item, index) => ({
              id:   index + 1,
              type: item.type || "activity",
              text: item.text || item.message || item.description || "Recent activity",
              time: item.createdAt
                ? `${new Date(item.createdAt).toLocaleTimeString()} ago`
                : "Just now",
              icon: item.type === "user"    ? FiUsers :
                    item.type === "task"    ? FiCheckSquare :
                    item.type === "message" ? FiMessageSquare :
                    item.type === "project" ? FiBriefcase :
                    FiActivity,
            }))
          : [];

        setStats((prev) => ({
          ...prev,
          totalUsers:     Array.isArray(users) ? users.length : 0,
          activeProjects: activeProjectsCount,
          pendingTasks:   pendingTasksCount,
          unreadMessages: unreadMessagesCount,
        }));

        setRecentActivity(
          formattedActivity.length > 0
            ? formattedActivity
            : [{ id: 1, type: "activity", text: "No recent activity", time: "Just now", icon: FiActivity }]
        );

      // ── EMPLOYEE ───────────────────────────────────────────────────────────
      } else {
        const userId = currentUser?._id || currentUser?.id;

        // ── 1. Pending tasks assigned to me ───────────────────────────────
        const tasksRes = await getTasks();
        const tasks    = tasksRes?.data?.data || tasksRes?.data || tasksRes || [];

        // Helper: extract string ID from string or object
        const extractId = (v) => {
          if (!v) return null;
          if (typeof v === "string") return v;
          if (typeof v === "object") return v._id || v.id || null;
          return null;
        };

        // Debug: log a sample to inspect shape
        if (Array.isArray(tasks) && tasks.length > 0) {
          console.log("📋 Sample task:", JSON.stringify(tasks[0], null, 2));
          console.log("👤 Current userId:", userId);
        }

        const myTasksCount = Array.isArray(tasks)
          ? tasks.filter((t) => {
              const status     = t.status?.toLowerCase();
              const assignedTo = t.assignedTo;
              const isDone     = status === "done" || status === "completed" || status === "finished";
              if (isDone) return false;

              // assignedTo can be: string, object, array of strings, array of objects
              if (Array.isArray(assignedTo)) {
                return assignedTo.some((a) => extractId(a) === userId);
              }
              return extractId(assignedTo) === userId;
            }).length
          : 0;

        console.log("✅ myTasksCount:", myTasksCount);

        // ── 2. Today's active hours from real logs ────────────────────────
        const todayStr  = formatLocalYYYYMMDD(new Date());
        const todayLogs = await fetchUserLogsForDate(userId, todayStr);
        const todayMs   = computeActiveMs(todayLogs, todayStr);
        const hoursToday = parseFloat((todayMs / (1000 * 60 * 60)).toFixed(2));

        // ── 3. Work days this month (days with active/remote/meeting log) ─
        // Fetch each day of the current month in parallel (batched to avoid 429)
        const monthDates = getDatesThisMonth();
        const BATCH      = 3;
        let workedDays   = 0;

        for (let i = 0; i < monthDates.length; i += BATCH) {
          const chunk   = monthDates.slice(i, i + BATCH);
          const results = await Promise.all(
            chunk.map((date) => fetchUserLogsForDate(userId, date))
          );
          results.forEach((logs) => {
            if (dayHasActiveWork(logs)) workedDays++;
          });
          // small delay between batches
          if (i + BATCH < monthDates.length) {
            await new Promise((r) => setTimeout(r, 200));
          }
        }

        // ── 4. Unread messages / contacts ────────────────────────────────
        const contactsRes    = await getAllContacts({ status: "pending" });
        const contacts       = contactsRes?.data || contactsRes || [];
        const myMessagesCount = Array.isArray(contacts) ? contacts.length : 0;

        // ── 5. Recent activity ────────────────────────────────────────────
        const activityRes  = await getLatestActivity();
        const activityData = activityRes?.data || activityRes || [];
        const formattedActivity = Array.isArray(activityData)
          ? activityData.slice(0, 5).map((item, index) => ({
              id:   index + 1,
              type: item.type || "activity",
              text: item.text || item.message || item.description || "Recent activity",
              time: item.createdAt
                ? `${new Date(item.createdAt).toLocaleTimeString()} ago`
                : "Just now",
              icon: item.type === "task"    ? FiCheckSquare :
                    item.type === "message" ? FiMessageSquare :
                    item.type === "project" ? FiBriefcase :
                    FiActivity,
            }))
          : [];

        setStats((prev) => ({
          ...prev,
          myTasks:       myTasksCount,
          myMessages:    myMessagesCount,
          hoursToday:    hoursToday,
          daysThisMonth: workedDays,
        }));

        setRecentActivity(
          formattedActivity.length > 0
            ? formattedActivity
            : [{ id: 1, type: "activity", text: "No recent activity", time: "Just now", icon: FiActivity }]
        );
      }
    } catch (err) {
      toast.error("Failed to load dashboard data");
      console.error("Dashboard data error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Quick links ──────────────────────────────────────────────────────────
  const adminLinks = [
    { title: "Clients",       desc: "Manage your clients",   icon: FiUsers,        path: "/clients",   color: "#3b82f6" },
    { title: "Projects",      desc: "View active projects",  icon: FiBriefcase,    path: "/projects",  color: "#f59e0b" },
    { title: "Tasks",         desc: "Team task board",       icon: FiCheckSquare,  path: "/tasks",     color: "#22c55e" },
    { title: "Messages",      desc: "Team communication",    icon: FiMessageSquare,path: "/messages",  color: "#8b5cf6" },
    { title: "Team Overview", desc: "Team performance",      icon: FiTrendingUp,   path: "/overview",  color: "#ec4899" },
    { title: "Requests",      desc: "Website inquiries",     icon: FiInbox,        path: "/requests",  color: "#06b6d4" },
  ];
  const founderLinks = [
    { title: "Finance", desc: "Payroll & expenses", icon: FiLayers, path: "/finance", color: "#f472b6" },
  ];
  const employeeLinks = [
    { title: "My Projects", desc: "Projects you are in",   icon: FiBriefcase,    path: "/projects",  color: "#f59e0b" },
    { title: "My Tasks",    desc: "Your assigned tasks",   icon: FiCheckSquare,  path: "/tasks",     color: "#22c55e" },
    { title: "Messages",    desc: "Chat with team",        icon: FiMessageSquare,path: "/messages",  color: "#8b5cf6" },
    { title: "Settings",    desc: "Profile & preferences", icon: FiUsers,        path: "/settings",  color: "#64748b" },
  ];

  const quickLinks = isAdmin
    ? (isFounder ? [...adminLinks, ...founderLinks] : adminLinks)
    : employeeLinks;

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    return "Good Evening";
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="home-page">
      {/* Welcome */}
      <div className="home-welcome">
        <div className="welcome-content">
          <h1>
            {getGreeting()}, <span className="welcome-name">{userName}</span>!
          </h1>
          <p className="welcome-role">{userRole} at Growth Station</p>
          <p className="welcome-date">
            <FiCalendar size={16} />
            {new Date().toLocaleDateString("en-GB", {
              weekday: "long", year: "numeric", month: "long", day: "numeric",
            })}
          </p>
        </div>
        <div className="welcome-avatar">{userName.charAt(0).toUpperCase()}</div>
      </div>

      {/* Stats */}
      <div className="home-stats">
        {isAdmin ? (
          <>
            <div className="stat-card" onClick={() => navigate("/users")}>
              <div className="stat-icon-wrapper" style={{ background: "rgba(59,130,246,0.15)" }}>
                <FiUsers size={24} color="#3b82f6" />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? "..." : stats.totalUsers}</span>
                <span className="stat-label">Total Users</span>
              </div>
              <FiArrowRight className="stat-arrow" size={20} />
            </div>

            <div className="stat-card" onClick={() => navigate("/projects")}>
              <div className="stat-icon-wrapper" style={{ background: "rgba(245,158,11,0.15)" }}>
                <FiBriefcase size={24} color="#f59e0b" />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? "..." : stats.activeProjects}</span>
                <span className="stat-label">Active Projects</span>
              </div>
              <FiArrowRight className="stat-arrow" size={20} />
            </div>

            <div className="stat-card" onClick={() => navigate("/tasks")}>
              <div className="stat-icon-wrapper" style={{ background: "rgba(34,197,94,0.15)" }}>
                <FiCheckSquare size={24} color="#22c55e" />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? "..." : stats.pendingTasks}</span>
                <span className="stat-label">Done Tasks</span>
              </div>
              <FiArrowRight className="stat-arrow" size={20} />
            </div>

            <div className="stat-card" onClick={() => navigate("/messages")}>
              <div className="stat-icon-wrapper" style={{ background: "rgba(139,92,246,0.15)" }}>
                <FiMessageSquare size={24} color="#8b5cf6" />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? "..." : stats.unreadMessages}</span>
                <span className="stat-label">Unread Messages</span>
              </div>
              <FiArrowRight className="stat-arrow" size={20} />
            </div>
          </>
        ) : (
          <>
            <div className="stat-card" onClick={() => navigate("/tasks")}>
              <div className="stat-icon-wrapper" style={{ background: "rgba(34,197,94,0.15)" }}>
                <FiCheckSquare size={24} color="#22c55e" />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? "..." : stats.myTasks}</span>
                <span className="stat-label">My Pending Tasks</span>
              </div>
              <FiArrowRight className="stat-arrow" size={20} />
            </div>

            <div className="stat-card" onClick={() => navigate("/messages")}>
              <div className="stat-icon-wrapper" style={{ background: "rgba(139,92,246,0.15)" }}>
                <FiMessageSquare size={24} color="#8b5cf6" />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? "..." : stats.myMessages}</span>
                <span className="stat-label">Unread Messages</span>
              </div>
              <FiArrowRight className="stat-arrow" size={20} />
            </div>

            <div className="stat-card">
              <div className="stat-icon-wrapper" style={{ background: "rgba(6,182,212,0.15)" }}>
                <FiClock size={24} color="#06b6d4" />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? "..." : (() => {
                  const hours = Math.floor(stats.hoursToday);
                  const minutes = Math.round((stats.hoursToday - hours) * 60);
                  return `${hours}h ${minutes}m`;
                })()}</span>
                <span className="stat-label">Active Hours Today</span>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="home-grid">
        {/* Quick Links */}
        <div className="home-section quick-links-section">
          <h2><FiActivity size={20} /> Quick Access</h2>
          <div className="quick-links-grid">
            {quickLinks.map((link) => (
              <button
                key={link.title}
                className="quick-link-card"
                onClick={() => navigate(link.path)}
                style={{ "--link-color": link.color }}
              >
                <div className="quick-link-icon" style={{ background: `${link.color}20`, color: link.color }}>
                  <link.icon size={22} />
                </div>
                <div className="quick-link-info">
                  <span className="quick-link-title">{link.title}</span>
                  <span className="quick-link-desc">{link.desc}</span>
                </div>
                <FiArrowRight size={16} className="quick-link-arrow" />
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="home-section activity-section">
          <h2><FiClock size={20} /> {isAdmin ? "Recent Activity" : "My Activity"}</h2>
          <div className="activity-list">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div
                  className="activity-icon"
                  style={{
                    background:
                      activity.type === "user"    ? "rgba(59,130,246,0.15)" :
                      activity.type === "task"    ? "rgba(34,197,94,0.15)"  :
                      activity.type === "message" ? "rgba(139,92,246,0.15)" :
                      activity.type === "project" ? "rgba(245,158,11,0.15)" :
                      "rgba(239,68,68,0.15)",
                    color:
                      activity.type === "user"    ? "#3b82f6" :
                      activity.type === "task"    ? "#22c55e" :
                      activity.type === "message" ? "#8b5cf6" :
                      activity.type === "project" ? "#f59e0b" :
                      "#ef4444",
                  }}
                >
                  <activity.icon size={16} />
                </div>
                <div className="activity-content">
                  <p className="activity-text">{activity.text}</p>
                  <span className="activity-time">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}